import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ShieldCheck, ShieldStar, MagnifyingGlass, Key, Warning, Stack, Trash, Plus, Users as UsersIcon } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/format";

const ROLE_BADGE = {
  super_admin: "bg-destructive/15 text-destructive border-destructive/40",
  owner: "bg-primary text-primary-foreground",
  admin: "bg-accent text-accent-foreground",
  director: "bg-primary text-primary-foreground",
};

export default function Permissions() {
  const { user: current, refresh } = useAuth();
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);   // {id, role, name, email, permissions[], granted_by_role[]}
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  // template dialog
  const [openTpl, setOpenTpl] = useState(false);
  const [tplForm, setTplForm] = useState({ name: "", description: "", permissions: [] });
  // apply dialog
  const [applyTpl, setApplyTpl] = useState(null);
  const [applySelection, setApplySelection] = useState([]);
  const [applyMode, setApplyMode] = useState("merge");

  const canPromote = ["super_admin","owner","admin","director"].includes(current?.role);

  const load = async () => {
    try {
      const [u, m, t] = await Promise.all([
        api.get("/users"),
        api.get("/modules"),
        api.get("/permission-templates").catch(() => ({ data: [] })),
      ]);
      setUsers(u.data); setModules(m.data.modules || []); setTemplates(t.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openUser = async (u) => {
    try {
      const { data } = await api.get(`/users/${u.id}/permissions`);
      setSelected(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const togglePermission = (key) => {
    setSelected(s => {
      const has = s.permissions.includes(key);
      return { ...s, permissions: has ? s.permissions.filter(p => p !== key) : [...s.permissions, key] };
    });
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const r = await api.put(`/users/${selected.user_id}/permissions`, { permissions: selected.permissions });
      const added = r.data.added || [], removed = r.data.removed || [];
      toast.success(`Permissions mises à jour · +${added.length} · −${removed.length}`);
      if (selected.user_id === current.id) await refresh();
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const promote = async (u) => {
    if (!window.confirm(`Promouvoir ${u.name} en administrateur de l'école ? Il aura accès à tous les modules.`)) return;
    try {
      await api.post(`/users/${u.id}/promote-admin`);
      toast.success(`${u.name} est désormais administrateur`);
      load(); if (selected?.user_id === u.id) openUser(u);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const revoke = async (u) => {
    if (!window.confirm(`Retirer le rôle admin à ${u.name} ? Il retrouvera son rôle précédent.`)) return;
    try {
      await api.post(`/users/${u.id}/revoke-admin`);
      toast.success("Rôle admin retiré");
      load(); if (selected?.user_id === u.id) openUser(u);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filteredUsers = useMemo(() => {
    const q = query.toLowerCase().trim();
    return users.filter(u => {
      if (filterRole !== "all" && u.role !== filterRole) return false;
      if (!q) return true;
      return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
    });
  }, [users, query, filterRole]);

  const roleOptions = useMemo(() => Array.from(new Set(users.map(u => u.role))).sort(), [users]);

  // ---------- Templates ----------
  const saveTemplate = async () => {
    if (!tplForm.name.trim()) { toast.error("Nom requis"); return; }
    try {
      await api.post("/permission-templates", tplForm);
      toast.success("Template créé");
      setOpenTpl(false);
      setTplForm({ name: "", description: "", permissions: [] });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteTemplate = async (t) => {
    if (!window.confirm(`Supprimer le template "${t.name}" ?`)) return;
    try { await api.delete(`/permission-templates/${t.id}`); toast.success("Template supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleTplPerm = (key) => {
    setTplForm(f => ({
      ...f,
      permissions: f.permissions.includes(key) ? f.permissions.filter(p => p !== key) : [...f.permissions, key],
    }));
  };
  const openApply = (t) => {
    setApplyTpl(t); setApplySelection([]); setApplyMode("merge");
  };
  const applyTemplate = async () => {
    if (!applyTpl || !applySelection.length) { toast.error("Choisissez au moins un utilisateur"); return; }
    try {
      const r = await api.post(`/permission-templates/${applyTpl.id}/apply`, {
        user_ids: applySelection, mode: applyMode,
      });
      toast.success(`Template appliqué à ${r.data.applied_to} utilisateur(s)`);
      setApplyTpl(null); setApplySelection([]);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleApplyUser = (uid_) => {
    setApplySelection(s => s.includes(uid_) ? s.filter(x => x !== uid_) : [...s, uid_]);
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <header className="mb-6">
        <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Sécurité</div>
        <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
          <ShieldStar size={32} weight="duotone" className="text-primary" />
          Rôles & Permissions
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Attribuez des permissions modulaires en plus du rôle de base — par exemple donner à un enseignant l'accès à la comptabilité, ou promouvoir un utilisateur en administrateur de l'école.
        </p>
      </header>

      <Tabs defaultValue="users">
        <TabsList data-testid="tabs-permissions">
          <TabsTrigger value="users" data-testid="tab-users">
            <UsersIcon size={14} weight="duotone" className="mr-1.5" /> Utilisateurs
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">
            <Stack size={14} weight="duotone" className="mr-1.5" /> Templates ({templates.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-6">
      <div className="grid lg:grid-cols-[1fr_1.3fr] gap-6">
        {/* Colonne gauche : liste utilisateurs */}
        <Card className="p-4">
          <div className="mb-3 flex items-center gap-2">
            <div className="relative flex-1">
              <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-7 h-9" placeholder="Rechercher un utilisateur…" value={query}
                     onChange={e => setQuery(e.target.value)} data-testid="input-search-user" />
            </div>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="h-9 w-36" data-testid="filter-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous rôles</SelectItem>
                {roleOptions.map(r => <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 max-h-[75vh] overflow-y-auto pr-1">
            {filteredUsers.map(u => {
              const isSelected = selected?.user_id === u.id;
              return (
                <button key={u.id} onClick={() => openUser(u)}
                        className={`w-full text-left px-3 py-2.5 rounded-md border transition ${isSelected ? "bg-primary/10 border-primary" : "border-transparent hover:bg-muted"}`}
                        data-testid={`btn-select-user-${u.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate text-sm">{u.name || u.email}</div>
                      <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                    </div>
                    <Badge variant="outline" className={`text-[10px] ${ROLE_BADGE[u.role] || ""}`}>
                      {ROLE_LABELS[u.role] || u.role}
                    </Badge>
                  </div>
                  {(u.permissions || []).length > 0 && (
                    <div className="mt-1.5 flex items-center gap-1 text-[10px] text-accent">
                      <Key size={10} weight="fill" /> +{u.permissions.length} permission(s)
                    </div>
                  )}
                </button>
              );
            })}
            {!filteredUsers.length && <div className="text-center text-sm text-muted-foreground py-8">Aucun utilisateur</div>}
          </div>
        </Card>

        {/* Colonne droite : éditeur */}
        {selected ? (
          <Card className="p-6" data-testid="permission-editor">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Utilisateur</div>
                <h2 className="mt-1 font-heading text-2xl font-bold">{selected.name}</h2>
                <div className="text-sm text-muted-foreground">{selected.email}</div>
                <Badge variant="outline" className={`mt-2 ${ROLE_BADGE[selected.role] || ""}`}>
                  Rôle: {ROLE_LABELS[selected.role] || selected.role}
                </Badge>
              </div>
              {canPromote && ["super_admin","owner"].indexOf(selected.role) === -1 && (
                selected.role === "admin" ? (
                  <Button variant="outline" onClick={() => revoke({ id: selected.user_id, name: selected.name })}
                          data-testid="btn-revoke-admin">
                    <Warning size={14} className="mr-2" /> Retirer admin
                  </Button>
                ) : (
                  <Button className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground"
                          onClick={() => promote({ id: selected.user_id, name: selected.name })}
                          data-testid="btn-promote-admin">
                    <ShieldStar size={14} className="mr-2" /> Promouvoir en admin école
                  </Button>
                )
              )}
            </div>

            <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
              Modules ({selected.granted_by_role.length} par rôle + {selected.permissions.length} ajoutés)
            </div>
            <div className="space-y-2">
              {modules.map(m => {
                const byRole = selected.granted_by_role.includes(m.key);
                const extra = selected.permissions.includes(m.key);
                const isAdminPanel = m.key === "admin_panel";
                const hidden = isAdminPanel && current?.role !== "super_admin" && !byRole;
                if (hidden) return null;
                const disabled = byRole || (isAdminPanel && selected.role !== "super_admin");
                return (
                  <label key={m.key}
                         className={`flex items-center justify-between gap-3 p-3 rounded-md border transition ${byRole ? "bg-primary/5 border-primary/30" : extra ? "bg-accent/5 border-accent/40" : "bg-muted/20 border-border hover:border-primary/40"} ${disabled && !byRole ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                         data-testid={`row-module-${m.key}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <ShieldCheck size={18} weight={byRole || extra ? "fill" : "regular"}
                                    className={byRole ? "text-primary" : extra ? "text-accent" : "text-muted-foreground"} />
                      <div className="min-w-0">
                        <div className="text-sm font-medium">{m.label}</div>
                        <div className="text-[10px] font-mono text-muted-foreground">{m.key}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {byRole && <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/30">rôle</Badge>}
                      {extra && <Badge variant="outline" className="text-[10px] bg-accent/10 text-accent border-accent/40">ajouté</Badge>}
                      <input type="checkbox"
                             checked={byRole || extra}
                             disabled={disabled}
                             onChange={() => togglePermission(m.key)}
                             className="w-4 h-4 accent-accent"
                             data-testid={`chk-perm-${m.key}`} />
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="mt-6 flex justify-end gap-2 sticky bottom-0 bg-background/90 pt-3">
              <Button variant="outline" onClick={() => setSelected(null)}>Fermer</Button>
              <Button onClick={save} disabled={saving} data-testid="btn-save-permissions">
                {saving ? "…" : "Enregistrer"}
              </Button>
            </div>
          </Card>
        ) : (
          <Card className="p-10 flex flex-col items-center justify-center text-center">
            <ShieldStar size={48} weight="duotone" className="text-muted-foreground/40 mb-3" />
            <div className="font-heading text-lg font-semibold">Choisissez un utilisateur</div>
            <div className="text-sm text-muted-foreground mt-1 max-w-md">
              Sélectionnez un utilisateur à gauche pour gérer ses permissions modulaires ou le promouvoir en administrateur.
            </div>
          </Card>
        )}
      </div>
        </TabsContent>

        {/* ---------- TAB TEMPLATES ---------- */}
        <TabsContent value="templates" className="mt-6 space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground max-w-2xl">
              Créez des ensembles de permissions réutilisables (ex: "Coordinateur pédagogique") et appliquez-les à plusieurs utilisateurs en un clic.
            </p>
            <Dialog open={openTpl} onOpenChange={setOpenTpl}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-new-template">
                  <Plus size={14} className="mr-1.5" /> Nouveau template
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Nouveau template de permissions</DialogTitle>
                  <DialogDescription>Sélectionnez les modules qui composent ce template.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div><label className="text-xs uppercase tracking-widest text-muted-foreground">Nom</label>
                    <Input value={tplForm.name} onChange={e => setTplForm({ ...tplForm, name: e.target.value })}
                           placeholder="Ex: Coordinateur pédagogique" data-testid="input-tpl-name" />
                  </div>
                  <div><label className="text-xs uppercase tracking-widest text-muted-foreground">Description</label>
                    <Textarea rows={2} value={tplForm.description} onChange={e => setTplForm({ ...tplForm, description: e.target.value })}
                              placeholder="À quoi sert ce template ?" data-testid="input-tpl-desc" />
                  </div>
                  <div><label className="text-xs uppercase tracking-widest text-muted-foreground">Modules ({tplForm.permissions.length})</label>
                    <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                      {modules.filter(m => !(m.key === "admin_panel" && current?.role !== "super_admin")).map(m => {
                        const active = tplForm.permissions.includes(m.key);
                        return (
                          <button key={m.key} type="button" onClick={() => toggleTplPerm(m.key)}
                                  className={`text-left p-2 rounded-md border text-xs transition ${active ? "bg-accent/10 border-accent" : "border-border hover:border-primary/40"}`}
                                  data-testid={`tpl-chk-${m.key}`}>
                            <div className="font-medium">{m.label}</div>
                            <div className="text-[9px] font-mono text-muted-foreground">{m.key}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpenTpl(false)}>Annuler</Button>
                  <Button onClick={saveTemplate} data-testid="btn-save-template">Créer</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {templates.length === 0 ? (
            <Card className="p-10 text-center">
              <Stack size={40} weight="duotone" className="text-muted-foreground/40 mx-auto mb-3" />
              <div className="text-sm text-muted-foreground">Aucun template pour le moment.</div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map(t => (
                <Card key={t.id} className="p-5" data-testid={`tpl-card-${t.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <div className="font-heading text-lg font-semibold truncate">{t.name}</div>
                      {t.description && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{t.description}</div>}
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => deleteTemplate(t)} data-testid={`btn-del-tpl-${t.id}`}>
                      <Trash size={14} className="text-destructive" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {(t.permissions || []).map(p => (
                      <Badge key={p} variant="outline" className="text-[10px] bg-accent/5 text-accent border-accent/30">{p}</Badge>
                    ))}
                    {!(t.permissions || []).length && <span className="text-xs text-muted-foreground">Aucun module</span>}
                  </div>
                  <Button variant="outline" size="sm" className="w-full" onClick={() => openApply(t)} data-testid={`btn-apply-tpl-${t.id}`}>
                    Appliquer à plusieurs utilisateurs
                  </Button>
                </Card>
              ))}
            </div>
          )}

          {/* Apply dialog */}
          <Dialog open={!!applyTpl} onOpenChange={(v) => !v && setApplyTpl(null)}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              {applyTpl && (
                <>
                  <DialogHeader>
                    <DialogTitle>Appliquer « {applyTpl.name} »</DialogTitle>
                    <DialogDescription>
                      Sélectionnez les utilisateurs qui recevront les permissions : {(applyTpl.permissions || []).join(", ") || "aucune"}.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-muted-foreground">Mode</label>
                      <Select value={applyMode} onValueChange={setApplyMode}>
                        <SelectTrigger data-testid="select-apply-mode"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="merge">Fusionner (ajouter aux permissions existantes)</SelectItem>
                          <SelectItem value="replace">Remplacer entièrement les permissions</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="max-h-[50vh] overflow-y-auto border border-border rounded-md">
                      {users.filter(u => !["super_admin","owner"].includes(u.role)).map(u => (
                        <label key={u.id} className={`flex items-center gap-2 p-2.5 border-b border-border last:border-0 cursor-pointer hover:bg-muted/50 ${applySelection.includes(u.id) ? "bg-accent/5" : ""}`}
                               data-testid={`apply-user-${u.id}`}>
                          <input type="checkbox" checked={applySelection.includes(u.id)} onChange={() => toggleApplyUser(u.id)} className="accent-accent" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{u.name || u.email}</div>
                            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{ROLE_LABELS[u.role] || u.role}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                    <div className="text-xs text-muted-foreground">{applySelection.length} utilisateur(s) sélectionné(s)</div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setApplyTpl(null)}>Annuler</Button>
                    <Button onClick={applyTemplate} data-testid="btn-confirm-apply-tpl">Appliquer</Button>
                  </DialogFooter>
                </>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
}
